import { useState } from "react";
import Form from "react-bootstrap/Form";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Layout from "../assets/component/Layout";

const BASE_URL = process.env.REACT_APP_API_URL


export default function Login() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");
  const [isAdminLogin, setIsAdminLogin] = useState(false);

  const togglePassword = () => setShowPassword((prev) => !prev);

  // Yup validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email format")
      .required("Email or username is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setApiError("");

      try {
        const response = await axios.post(
          `${BASE_URL}login`,
          {
            email: values.email,
            password: values.password,
            is_user: !isAdminLogin,
          }
        );
        if (!response.data.success) {
          setApiError(response.data.message || "Login failed. Please try again.");
          return; // Stop further execution
        }

        localStorage.setItem("token", response.data.data?.token);
        localStorage.setItem("role", response.data.data?.role);
        const routeUrl = response.data.data?.role == 'admin' ? '/all-users' : '/home'
        navigate(routeUrl);

      } catch (error) {
        // Handle true errors (network, server crash, etc.)
        console.error("Login failed:", error);
        if (error.response && error.response.data?.message) {
          setApiError(error.response.data.message);
        } else {
          setApiError("Something went wrong. Please try again.");
        }
      }
    },
  });

  return (
    <Layout>
      <section className="login-layout">
        <div className="main-login-form">
          <h1>Log In</h1>
          <p>Fill in your details for login into account.</p>
          <Form onSubmit={formik.handleSubmit} >
            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                name="email"
                placeholder="Enter your email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.email && !!formik.errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.email}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="formBasicEmail">
              <Form.Label>Password</Form.Label>

              <div className="adding-eye-icon">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.password && !!formik.errors.password}
                />

                <div onClick={togglePassword} style={{ cursor: "pointer" }}>
                  {showPassword ? (
                    // Hide Icon
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M21.544 11.045C21.848 11.4713 22 11.6845 22 12C22 12.3155 21.848 12.5287 21.544 12.955C20.1779 14.8706 16.6892 19 12 19C7.31078 19 3.8221 14.8706 2.45604 12.955C2.15201 12.5287 2 12.3155 2 12C2 11.6845 2.15201 11.4713 2.45604 11.045C3.8221 9.12944 7.31078 5 12 5C16.6892 5 20.1779 9.12944 21.544 11.045Z"
                        stroke="#545454"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12Z"
                        stroke="#545454"
                        strokeWidth="1.5"
                      />
                    </svg>
                  ) : (
                    // Show Icon
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19.439 15.439C20.3636 14.5212 21.0775 13.6091 21.544 12.955C21.848 12.5287 22 12.3155 22 12C22 11.6845 21.848 11.4713 21.544 11.045C20.1779 9.12944 16.6892 5 12 5C11.0922 5 10.2294 5.15476 9.41827 5.41827M6.74742 6.74742C4.73118 8.1072 3.24215 9.94266 2.45604 11.045C2.15201 11.4713 2 11.6845 2 12C2 12.3155 2.15201 12.5287 2.45604 12.955C3.8221 14.8706 7.31078 19 12 19C13.9908 19 15.7651 18.2557 17.2526 17.2526"
                        stroke="#545454"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9.85786 10C9.32783 10.53 9 11.2623 9 12.0711C9 13.6887 10.3113 15 11.9289 15C12.7377 15 13.47 14.6722 14 14.1421"
                        stroke="#545454"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M3 3L21 21"
                        stroke="#545454"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </Form.Group>
            {/* <Form.Group
              className="mb-4 check-w-forgot"
              controlId="formBasicCheckbox"
            >
              <Link to="/forgot-password">Forgot Password?</Link>
            </Form.Group> */}
            <Form.Group className="mb-3 check-w-forgot d-flex justify-content-between align-items-center" controlId="formBasicCheckbox">
              <>
                <Form.Check
                  type="checkbox"
                  label="Login as Admin"
                  checked={isAdminLogin}
                  onChange={() => setIsAdminLogin(prev => !prev)}
                />
              </>
              <Link to="/forgot-password">Forgot Password?</Link>
            </Form.Group>
            <button >Login</button>
          </Form>
        </div>
      </section>
    </Layout>
  );
}
